import React, { type FC, useMemo, useRef, useState } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import { BusinessForm, type FormHandle } from "@src/components/business-form";
import {
    BusinessLotValue,
    BusinessMeasureValue,
    BusinessStockObjValue,
    formatLotText,
    formatStockObjText,
} from "@src/components/business-data-display";
import { StorePlaceCellPickerField } from "@src/businessModules/Stock/components/StorePlaceCellPickerField/StorePlaceCellPickerField";
import { groupSpMovingsByCell, sumSpMovingQty, type SpMovingCellGroup } from "../../../../../../api/storePlaces";
import { DetailList } from "@shared/ui/detail-row";
import { EmptyState } from "@shared/ui/empty-state";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel, type FormPanelRenderProps } from "@shared/ui/form-panel";
import type { FinishedGoodsReceiptContent } from "../../../../../../api/finishedGoodsReceipts";
import type { UseStockFinishedGoodsReceiptCardMutationsResult } from "../../useStockFinishedGoodsReceiptCardMutations";
import type { ConfirmStockFinishedGoodsReceiptCancelParams } from "../../useStockFinishedGoodsReceiptCancelConfirm";
import { useStockFinishedGoodsReceiptContentsQuery } from "../../hooks";
import {
    formatCellName,
    formatOrderText,
    formatPlacedCellsFromSpMovings,
    formatRecommendedCells,
} from "../../helpers/formatFinishedGoodsReceiptContent";
import {
    stockFinishedGoodsReceiptContentFormConfig,
    type StockFinishedGoodsReceiptContentFormValues,
} from "./stockFinishedGoodsReceiptContent.form-config";
import { resolveFinishedGoodsReceiptCellScan } from "./resolveFinishedGoodsReceiptCellScan";
import "./StockFinishedGoodsReceiptContentForm.scss";

const CELL_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-finished-goods-receipt.content.scan-cell",
    title: "Сканируйте штрихкод ячейки размещения",
    description: "Найдём доступную ячейку СГП и выберем её для размещения продукции.",
    position: { placement: "bottom-left", offset: { y: -40 } },
    initialCollapsed: true,
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockFinishedGoodsReceiptContentForm}. */
export interface StockFinishedGoodsReceiptContentFormProps {
    /** Код ВЗН УП, которому принадлежит строка. */
    inplantCode: number;
    /** Код склада СГП для поиска ячеек. */
    divisionCode: number;
    /** Код строки ВЗН УП. */
    contentCode: number;
    /** Мутация размещения продукции по `ArrivalSdbCode`. */
    placeBySdbMutation: UseStockFinishedGoodsReceiptCardMutationsResult["placeBySdbMutation"];
    /**
     * Подтверждение и отмена размещения в ячейке.
     * @param params параметры отмены с полями для confirm
     */
    confirmAndCancelPlacement: (params: ConfirmStockFinishedGoodsReceiptCancelParams) => Promise<void>;
    /**
     * Признак выполнения отмены размещения.
     *
     * TODO(overlay): снимок из `windows.open` — `cancelPlacementBySdbMutation.isPending` с карточки
     * не обновляется в окне. Сейчас частично спасает локальный `cancelingCellCode`.
     * Пересмотреть: `useIsMutating({ mutationKey })` в форме или локальный pending вокруг cancel.
     */
    isCancelPlacementPending: boolean;
}

/** Форма размещения продукции по строке ВЗН УП. */
export const StockFinishedGoodsReceiptContentForm: FC<StockFinishedGoodsReceiptContentFormProps> = ({
    inplantCode,
    divisionCode,
    contentCode,
    placeBySdbMutation,
    confirmAndCancelPlacement,
    isCancelPlacementPending,
}) => {
    const contentsQuery = useStockFinishedGoodsReceiptContentsQuery(inplantCode, divisionCode, {
        refetchOnMount: false,
    });
    const content = contentsQuery.content.find((item) => item.Code === contentCode);

    if (content == null) {
        return <EmptyState text="Строка ВЗН УП не найдена" />;
    }

    return (
        <StockFinishedGoodsReceiptContentFormBody
            divisionCode={divisionCode}
            content={content}
            placeBySdbMutation={placeBySdbMutation}
            confirmAndCancelPlacement={confirmAndCancelPlacement}
            isCancelPlacementPending={isCancelPlacementPending || contentsQuery.isFetching}
        />
    );
};

interface StockFinishedGoodsReceiptContentFormBodyProps {
    divisionCode: number;
    content: FinishedGoodsReceiptContent;
    placeBySdbMutation: UseStockFinishedGoodsReceiptCardMutationsResult["placeBySdbMutation"];
    confirmAndCancelPlacement: (params: ConfirmStockFinishedGoodsReceiptCancelParams) => Promise<void>;
    isCancelPlacementPending: boolean;
}

const StockFinishedGoodsReceiptContentFormBody: FC<StockFinishedGoodsReceiptContentFormBodyProps> = ({
    divisionCode,
    content,
    placeBySdbMutation,
    confirmAndCancelPlacement,
    isCancelPlacementPending,
}) => {
    const formApiRef = useRef<FormHandle<StockFinishedGoodsReceiptContentFormValues> | null>(null);
    const [cancelingCellCode, setCancelingCellCode] = useState<number | null>(null);
    const placedQty = sumSpMovingQty(content.SpMovings);
    /** Цель размещения — `ArrivalQty` (поле не обновляется беком после `spMoving.insert`). */
    const targetQty = content.ArrivalQty;
    const placeQty = Math.max(targetQty - placedQty, 0);
    const isFullyPlaced = targetQty > 0 && placedQty >= targetQty;
    const arrivalSdbCodeIsValidForPlacement = isValidCode(content.ArrivalSdbCode);
    const formDisabled = isFullyPlaced || !arrivalSdbCodeIsValidForPlacement || placeQty <= 0;
    const placedCells = groupSpMovingsByCell(content.SpMovings);

    const initialValues = useMemo((): StockFinishedGoodsReceiptContentFormValues => ({ cellCode: null }), []);

    const handleSubmit = async (values: StockFinishedGoodsReceiptContentFormValues): Promise<void> => {
        if (isFullyPlaced || placeQty <= 0) {
            return;
        }

        if (!isValidCode(content.ArrivalSdbCode)) {
            notification.warning("Для строки ВЗН УП не задан код приходного движения размещения.");
            return;
        }

        if (values.cellCode == null) {
            return;
        }

        await placeBySdbMutation.mutateAsync({
            sdbCode: content.ArrivalSdbCode,
            cellCode: values.cellCode,
            qty: placeQty,
            contentCode: content.Code,
        });
    };

    const handleCancelPlacement = async (placedCell: SpMovingCellGroup): Promise<void> => {
        if (!isValidCode(content.ArrivalSdbCode)) {
            notification.warning("Для строки ВЗН УП не задан код приходного движения размещения.");
            return;
        }

        const cellLabel = placedCell.StorePlace?.Num;

        setCancelingCellCode(placedCell.SpCode);
        try {
            await confirmAndCancelPlacement({
                sdbCode: content.ArrivalSdbCode,
                cellCode: placedCell.SpCode,
                contentCode: content.Code,
                fields: [
                    { label: "Ячейка", value: cellLabel },
                    { label: "Партия прихода", value: formatLotText(content.ArrivalLot) },
                    { label: "Номенклатура", value: formatStockObjText(content.StockObj) },
                ],
            });
        } finally {
            setCancelingCellCode(null);
        }
    };

    return (
        <FormPanel<StockFinishedGoodsReceiptContentFormValues>
            formApiRef={formApiRef}
            useDefaultFooter={false}
            disabled={formDisabled}
            formProps={{
                formConfig: stockFinishedGoodsReceiptContentFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={({ submitForm, isFormSubmitting }) => (
                <FormFooter
                    confirmText="Разместить"
                    confirmLoading={isFormSubmitting}
                    confirmDisabled={formDisabled}
                    onConfirm={submitForm}
                />
            )}
        >
            {(form) => (
                <div className="stock_finished_goods_receipt_content_form">
                    <StockFinishedGoodsReceiptCellScanRegistration
                        form={form}
                        divisionCode={divisionCode}
                        showHint={!formDisabled}
                        paused={
                            formDisabled ||
                            form.isFormSubmitting === true ||
                            isCancelPlacementPending ||
                            cancelingCellCode != null
                        }
                    />
                    <DetailList layout="tiles" columns="auto" minWidth={112}>
                        <DetailList.Row label="Номенклатура" value={<BusinessStockObjValue stockObj={content.StockObj} />} />
                        <DetailList.Row label="Заказ" value={formatOrderText(content.Order)} />
                        <DetailList.Row label="Партия прихода" value={<BusinessLotValue lot={content.ArrivalLot} />} />
                        <DetailList.Row
                            label="Рекомендуемые ячейки"
                            value={formatRecommendedCells(content.RecommendedCells)}
                        />
                        <DetailList.Row
                            label="Размещено"
                            tone={isFullyPlaced ? "success" : "default"}
                            value={<BusinessMeasureValue value={`${placedQty} / ${targetQty}`} measure={content.Measure} />}
                        />
                        <DetailList.Row
                            label="К размещению"
                            value={<BusinessMeasureValue value={placeQty} measure={content.Measure} />}
                        />
                        <DetailList.Row
                            label="Ячейки размещения"
                            value={formatPlacedCellsFromSpMovings(content.SpMovings)}
                        />
                    </DetailList>
                    {isFullyPlaced && <EmptyState text="Строка ВЗН УП уже размещена" />}
                    {!arrivalSdbCodeIsValidForPlacement && (
                        <EmptyState text="Для строки ВЗН УП не задан код приходного движения размещения." />
                    )}
                    <div className="stock_finished_goods_receipt_content_form__fields">
                        <BusinessForm.Field name="cellCode">
                            <StorePlaceCellPickerField
                                divisionCode={divisionCode}
                                recommendedCells={content.RecommendedCells}
                                value={form.getValues("cellCode")}
                                onChange={(cellCode) => {
                                    form.setValue("cellCode", cellCode, { shouldDirty: true });
                                }}
                                label={form.field("cellCode").getUiProps().label}
                                required={form.field("cellCode").getUiProps().required}
                                disabled={form.field("cellCode").getUiProps().disabled}
                                error={form.getFieldState("cellCode").error != null}
                            />
                        </BusinessForm.Field>
                    </div>

                    {placedCells.length > 0 && (
                        <section
                            className="stock_finished_goods_receipt_content_form__placed"
                            aria-label="Размещённые ячейки"
                        >
                            <h3 className="stock_finished_goods_receipt_content_form__placed_title">Размещённые ячейки</h3>
                            <DetailList layout="tiles" oneLine={true}>
                                {placedCells.map((placedCell) => {
                                    return (
                                        <DetailList.Row
                                            key={placedCell.SpCode}
                                            label={formatCellName(placedCell.StorePlace)}
                                            value={<BusinessMeasureValue value={placedCell.Qty} measure={content.Measure} />}
                                            onClick={() => {
                                                const disabled = isCancelPlacementPending || cancelingCellCode != null;
                                                if (!disabled) {
                                                    void handleCancelPlacement(placedCell);
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </DetailList>
                        </section>
                    )}
                </div>
            )}
        </FormPanel>
    );
};

/** Пропсы регистрации сканера ячейки внутри формы размещения. */
interface StockFinishedGoodsReceiptCellScanRegistrationProps {
    /** API формы размещения продукции. */
    form: FormPanelRenderProps<StockFinishedGoodsReceiptContentFormValues>;
    /** Код склада СГП. */
    divisionCode: number;
    /**
     * Показывать ли app-level hint сканирования ячейки. \
     * При полностью размещённой строке hint скрыт, но registration остаётся topmost.
     */
    showHint: boolean;
    /** Временная пауза scanner-сценария формы. */
    paused: boolean;
}

/** Регистрирует сценарий сканирования ячейки в окне размещения продукции. */
const StockFinishedGoodsReceiptCellScanRegistration: FC<StockFinishedGoodsReceiptCellScanRegistrationProps> = ({
    form,
    divisionCode,
    showHint,
    paused,
}) => {
    const handleScan = React.useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveFinishedGoodsReceiptCellScan(barcode, divisionCode);

            if (result.type === "notFound") {
                notification.warning(`Ячейка размещения для скана "${barcode}" не найдена на складе СГП.`);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning(`Для скана "${barcode}" найдено несколько ячеек. Выберите ячейку вручную.`);
                return;
            }

            form.setValue("cellCode", result.cell.Code, { shouldDirty: true });
            notification.success(`Ячейка "${result.cell.Num}" выбрана для размещения.`);
        },
        [divisionCode, form],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            paused,
            priority: "window",
            captureInEditable: true,
            hint: showHint ? CELL_SCAN_HINT : undefined,
            onScan: handleScan,
            errorSource: "stock.finishedGoodsReceipt.content.scanCell",
        }),
        [handleScan, paused, showHint],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};

function isValidCode(code: number): boolean {
    return code !== 0;
}
